// src/ui/auth/PhoneAuthPage.jsx

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCountryByDialCode } from '../../utils/countries';
import authService, { OtpRateLimitException } from '../../services/auth/authService';

export const PhoneAuthPage = ({ isSignInFlow = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [code, setCode] = useState('+');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showDivider, setShowDivider] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [toast, setToast] = useState(null); // { message }

  const codeInputRef = useRef(null);
  const numberInputRef = useRef(null);
  const toastTimerRef = useRef(null);

  // ── Computed ──────────────────────────────────────────────────────────────

  const getFullPhone = () => {
    if (!selectedCountry) return '';
    const raw = phoneNumber.trim();
    const normalized = raw.startsWith('0') ? raw.substring(1) : raw;
    return `+${selectedCountry.dialCode}${normalized}`;
  };

  const isValid = selectedCountry && phoneNumber.trim().length >= 8;

  // ── Toast (mirrors ScaffoldMessenger snackbar) ──────────────────────────────

  const showToast = (message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

    // Clean up reCAPTCHA widget when leaving the page
  useEffect(() => {
    return () => {
      authService._resetRecaptcha();
    };
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleBack = () => {
    navigate(-1);
  };

  const handlePickCountry = () => {
    navigate('/auth/country', {
      state: { returnTo: '/auth/phone' }
    });
  };

  const applySelectedCountry = (country) => {
    setSelectedCountry(country);
    setCode(`+${country.dialCode}`);
    setShowDivider(true);
    setTimeout(() => {
      numberInputRef.current?.focus();
    }, 0);
  };

  // Pick up the country returned from CountryPickerPage (mirrors
  // `await Navigator.push<Country>` in the Flutter _pickCountry)
  useEffect(() => {
    const returnedCountry = location.state?.selectedCountry;
    if (returnedCountry) {
      applySelectedCountry(returnedCountry);
      // Clear it so refreshing / re-navigating doesn't re-apply it
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const detectCountryFromCode = (value) => {
    if (isAutoFilling) return;

    if (!value.startsWith('+')) {
      setCode(`+${value}`);
      return;
    }

    const codeWithoutPlus = value.substring(1);
    const country = getCountryByDialCode(codeWithoutPlus);

    if (country) {
      setSelectedCountry(country);
      setShowDivider(true);

      if (!isAutoFilling && codeWithoutPlus.length > 0) {
        setIsAutoFilling(true);
        setTimeout(() => {
          numberInputRef.current?.focus();
          setIsAutoFilling(false);
        }, 100);
      }
    }
  };

  const handleCodeFieldClick = () => {
    if (!code || code === '') {
      setCode('+');
    } else if (!code.startsWith('+')) {
      setCode(`+${code}`);
    }
  };

  const startVerification = async () => {
    if (!isValid || isSending) return;

    setIsSending(true);

    try {
      const fullPhone = getFullPhone();
      const otpData = await authService.sendFirebaseOtp(fullPhone);

      navigate('/auth/verify-otp', {
        state: {
          phoneNumber: fullPhone,
          verificationId: otpData.verificationId,
          resendToken: otpData.resendToken,
          isSignInFlow: isSignInFlow,
        }
      });
    } catch (err) {
      // Surface the real Firebase error code while debugging — remove
      // this console line once phone auth is confirmed stable in prod.
      console.error('[PhoneAuth] raw error:', err?.code, err?.message, err);

      if (err instanceof OtpRateLimitException) {
        showToast(`Too many attempts. Please wait ${err.formattedRemaining} before trying again.`);
      } else {
        const msg = err.message || err.toString();
        if (msg.includes('invalid-phone-number')) {
          showToast('The phone number entered is invalid.');
        } else if (msg.includes('too-many-requests')) {
          showToast('Too many requests from this device. Please wait a few minutes and try again.');
        } else if (msg.includes('quota-exceeded')) {
          showToast('SMS quota exceeded. Please try again later.');
        } else if (msg.includes('captcha-check-failed') || msg.includes('argument-error')) {
          showToast('Verification setup failed. Please refresh the page and try again.');
        } else {
          showToast('Failed to send code. Please try again.');
        }
      }
    } finally {
      setIsSending(false);
    }
  };

  // ── Init ──────────────────────────────────────────────────────────────────
  // Only reset to the bare '+' prompt when we're NOT arriving with a country
  // already picked (otherwise this fires after the location.state effect
  // above and wipes out the code it just set).

  useEffect(() => {
    if (location.state?.selectedCountry) return;

    setCode('+');
    setTimeout(() => {
      codeInputRef.current?.focus();
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-filatom-bg relative">
      <div className="max-w-md mx-auto px-6 h-screen flex flex-col">
        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto pb-24">
          <div className="pt-10">
            <h1 className="text-white text-xl font-medium text-center">Phone number</h1>
            <p className="text-white/50 text-[15px] leading-tight text-center mt-2">
              Please confirm your country code<br />and enter your phone number.
            </p>
          </div>

          {/* Phone Input Card */}
          <div className="mt-8">
            <div className="bg-filatom-card rounded-[20px] overflow-hidden">
              {/* Country Selector */}
              <button
                onClick={handlePickCountry}
                className="w-full flex items-center justify-between px-4 py-2 text-white text-[15px] hover:bg-white/[0.03] transition-colors"
              >
                <span>
                  {selectedCountry
                    ? `${selectedCountry.flag} ${selectedCountry.name}`
                    : 'Country'}
                </span>
                <svg className="w-[22px] h-[22px] text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <div className="h-px bg-white/10 mx-4" />

              {/* Code + Number */}
              <div className="flex items-center px-4 py-2">
                <div className="w-[70px] flex-shrink-0">
                  <input
                    ref={codeInputRef}
                    type="text"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value);
                      detectCountryFromCode(e.target.value);
                    }}
                    onClick={handleCodeFieldClick}
                    className="w-full bg-transparent text-white text-[15px] outline-none py-2"
                    placeholder="+"
                  />
                </div>

                {showDivider && (
                  <div className="w-px h-[22px] bg-filatom-primary mx-2 flex-shrink-0" />
                )}

                <div className="flex-1">
                  <input
                    ref={numberInputRef}
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setPhoneNumber(val);
                    }}
                    placeholder={showDivider ? 'Phone number' : ''}
                    disabled={!showDivider}
                    className="w-full bg-transparent text-white text-[15px] outline-none placeholder:text-white/30 py-2 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed bottom button */}
        <div className="absolute left-6 right-6 bottom-5 max-w-md mx-auto w-[calc(100%-3rem)]">
          <button
            onClick={startVerification}
            disabled={!isValid || isSending}
            className={`w-full py-3 rounded-2xl text-sm font-medium transition-all ${
              isValid && !isSending
                ? 'text-white hover:opacity-90 active:scale-98'
                : 'bg-filatom-card text-gray-500 cursor-not-allowed'
            }`}
            style={isValid && !isSending ? { backgroundColor: '#FF8C08' } : undefined}
          >
            {isSending ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Sending...</span>
              </div>
            ) : (
              'Next'
            )}
          </button>
        </div>

        {/* Back button (top-left, since PopScope/back gesture isn't native on web) */}
        <button
          onClick={handleBack}
          className="absolute top-4 left-0 p-2 text-white/70 hover:text-white transition-colors"
          aria-label="Back"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* reCAPTCHA mount point — required by Firebase's signInWithPhoneNumber.
          size: 'invisible' in authService means this renders nothing visible
          under normal conditions, so it's safe to leave sitting here. */}

      {/* Snackbar-style toast (mirrors ScaffoldMessenger.showSnackBar) */}
      {toast && (
        <div className="fixed left-4 right-4 bottom-6 max-w-md mx-auto z-50">
          <div
            className="rounded-xl px-4 py-3 shadow-lg"
            style={{ backgroundColor: '#2C1A0E' }}
          >
            <p className="text-white text-sm text-center">{toast.message}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneAuthPage;